import { FC, useEffect, useRef, useState } from 'react'
import { required, SelectInput, useDataProvider } from 'react-admin'
import { useFormContext } from 'react-hook-form'

const INTERFACES_PER_PAGE = 100

interface InterfaceItem {
  id: number
  uuid: string
  name: string
  module: string
  active: number
  interface_config?: Record<string, unknown> | null
}

interface Props {
  instanceId?: string
  module?: string
}

const CertificatesInterfaceSelect: FC<Props> = ({ instanceId, module }) => {
  const dataProvider = useDataProvider()
  const { setValue, getValues } = useFormContext()
  const [choices, setChoices] = useState<InterfaceItem[]>([])
  const [loading, setLoading] = useState(false)
  const previous = useRef({ instanceId, module })

  // Selection belongs to the previous environment/module: drop it. Also on mount, when a
  // remount (e.g. type switch) left an interface of another module, like stufconfig on samlidp.
  useEffect(() => {
    const current = getValues('metaData.interface') as InterfaceItem | null | undefined
    const changed = previous.current.instanceId !== instanceId || previous.current.module !== module
    previous.current = { instanceId, module }
    if (changed || (current && current.module !== module)) {
      setValue('metaData.interface', null)
    }
  }, [instanceId, module, setValue, getValues])

  useEffect(() => {
    setChoices([])
    if (!instanceId || !module) {
      return
    }

    // Ignore responses of superseded requests (A -> B, A answers last).
    let cancelled = false
    setLoading(true)
    void dataProvider
      .getList<InterfaceItem>('interfaces', {
        // eslint-disable-next-line camelcase
        filter: { instance_id: instanceId, module },
        pagination: { page: 1, perPage: INTERFACES_PER_PAGE },
        sort: { field: 'id', order: 'DESC' },
      })
      .then(({ data }) => {
        if (!cancelled) setChoices(data)
      })
      .catch(() => {
        if (!cancelled) setChoices([])
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [instanceId, module, dataProvider])

  return (
    <SelectInput
      source="metaData.interface"
      label="resources.certificates.fields.interface"
      choices={choices}
      optionText="name"
      optionValue="id"
      fullWidth
      disabled={loading || !instanceId || !module}
      validate={[required()]}
      parse={(value: number | null) =>
        value == null ? null : choices.find((c) => c.id === value) ?? null
      }
      format={(value: InterfaceItem | number | null | undefined) =>
        value && typeof value === 'object' && 'id' in value
          ? value.id
          : value
      }
    />
  )
}

export default CertificatesInterfaceSelect
